import React, { useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import storage from '../utils/storage';
import { getBackendUrl } from '../utils/api';
import { useLanguage } from '../contexts/LanguageContext';

const TrialSignup = () => {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    password: '',
    confirmPassword: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [trialInfo, setTrialInfo] = useState(null);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // Validation
    if (!formData.username || !formData.email || !formData.password) {
      setError('Όλα τα πεδία είναι υποχρεωτικά');
      return;
    }

    if (formData.password !== formData.confirmPassword) {
      setError('Οι κωδικοί δεν ταιριάζουν');
      return;
    }

    if (formData.password.length < 6) {
      setError('Ο κωδικός πρέπει να έχει τουλάχιστον 6 χαρακτήρες');
      return;
    }

    setLoading(true);

    try {
      const BACKEND_URL = getBackendUrl();
      if (!BACKEND_URL) {
        throw new Error('Backend URL not configured.');
      }
      const response = await axios.post(`${BACKEND_URL}/api/auth/trial-signup`, {
        username: formData.username,
        email: formData.email,
        password: formData.password
      });

      setTrialInfo(response.data);
      
      // Store token and redirect to home after 3 seconds
      storage.setItem('token', response.data.token);
      storage.setItem('user', JSON.stringify({
        username: response.data.username,
        email: response.data.email,
        role: 'trial'
      }));
      
      setTimeout(() => {
        navigate('/');
      }, 3000);

    } catch (err) {
      setError(err.response?.data?.detail || 'Σφάλμα κατά τη δημιουργία λογαριασμού');
    } finally {
      setLoading(false);
    }
  };

  if (trialInfo) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-600 to-blue-600 flex items-center justify-center p-4">
        <div className="bg-white rounded-lg shadow-2xl p-8 w-full max-w-md">
          <div className="text-center">
            <div className="mb-4">
              <div className="w-20 h-20 bg-green-500 rounded-full flex items-center justify-center mx-auto">
                <svg className="w-12 h-12 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                </svg>
              </div>
            </div>
            
            <h2 className="text-2xl font-bold text-gray-800 mb-2">
              🎉 Επιτυχής Εγγραφή!
            </h2>
            
            <p className="text-gray-600 mb-4">
              {t('trialCreatedSuccess')}
            </p>

            <div className="bg-gradient-to-r from-purple-100 to-blue-100 rounded-lg p-6 mb-4">
              <div className="text-3xl font-bold text-purple-600 mb-2">
                ⏰ 24 {t('hours') || 'Hours'}
              </div>
              <div className="text-sm text-gray-600">
                {t('trialAccountDesc')}
              </div>
            </div>

            <div className="text-left bg-gray-50 rounded-lg p-4 mb-4 text-sm">
              <div className="mb-2">
                <span className="font-semibold">{t('username')}:</span> {trialInfo.username}
              </div>
              <div className="mb-2">
                <span className="font-semibold">{t('email')}:</span> {trialInfo.email}
              </div>
              <div>
                <span className="font-semibold">{t('expires') || 'Expires'}:</span> {new Date(trialInfo.expires_at).toLocaleString('el-GR')}
              </div>
            </div>

            <div className="text-sm text-gray-500 mb-4">
              {t('redirectingToLogin') || 'Redirecting to login page...'}
            </div>

            <div className="w-full bg-gray-200 rounded-full h-2">
              <div className="bg-gradient-to-r from-purple-600 to-blue-600 h-2 rounded-full animate-pulse" style={{width: '100%'}}></div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-600 to-blue-600 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-2xl p-8 w-full max-w-md">
        <div className="text-center mb-6">
          <h2 className="text-3xl font-bold text-gray-800 mb-2">
            👤 {t('accountAccess') || 'Είσοδος / Εγγραφή'}
          </h2>
          <p className="text-gray-600">
            {t('newUserTrialDesc') || 'Νέος χρήστης; Δημιούργησε λογαριασμό με 24ωρη δωρεάν πρόσβαση!'}
          </p>
        </div>

        <div className="bg-gradient-to-r from-purple-100 to-blue-100 rounded-lg p-4 mb-6">
          <h3 className="font-bold text-purple-700 mb-2">✨ {t('whatIsIncluded') || 'What\'s included'}:</h3>
          <ul className="text-sm text-gray-700 space-y-1">
            <li>✅ {t('fullAccess') || 'Full access to all features'}</li>
            <li>✅ {t('radioContentCreation') || 'Radio content creation'}</li>
            <li>✅ {t('chatbotSupport') || 'Chatbot support'}</li>
            <li>✅ {t('trialExpiresIn')}</li>
            <li>⚠️ {t('accountLocksAfter') || 'Account locks after 24 hours'}</li>
          </ul>
        </div>

        {error && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-gray-700 font-semibold mb-2">
              {t('username')}
            </label>
            <input
              type="text"
              name="username"
              value={formData.username}
              onChange={handleChange}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
              placeholder={t('chooseUsername')}
              required
            />
          </div>

          <div>
            <label className="block text-gray-700 font-semibold mb-2">
              {t('email')}
            </label>
            <input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
              placeholder="email@example.com"
              required
            />
          </div>

          <div>
            <label className="block text-gray-700 font-semibold mb-2">
              {t('password')}
            </label>
            <input
              type="password"
              name="password"
              value={formData.password}
              onChange={handleChange}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
              placeholder={t('minChars')}
              required
            />
          </div>

          <div>
            <label className="block text-gray-700 font-semibold mb-2">
              {t('confirmPassword')}
            </label>
            <input
              type="password"
              name="confirmPassword"
              value={formData.confirmPassword}
              onChange={handleChange}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
              placeholder={t('repeatPassword')}
              required
            />
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full bg-gradient-to-r from-purple-600 to-blue-600 text-white font-bold py-3 px-4 rounded-lg hover:from-purple-700 hover:to-blue-700 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? (
              <span className="flex items-center justify-center">
                <svg className="animate-spin h-5 w-5 mr-3" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                </svg>
                {t('creatingAccount')}
              </span>
            ) : (
              `🎁 ${t('createTrialAccount')}`
            )}
          </button>
        </form>

        <div className="mt-6 text-center border-t border-gray-200 pt-6">
          <p className="text-gray-700 font-medium mb-3">{t('alreadyHaveAccount') || 'Έχετε ήδη λογαριασμό;'}</p>
          <button
            onClick={() => navigate('/login')}
            className="w-full bg-gradient-to-r from-green-600 to-emerald-600 text-white font-bold py-3 px-4 rounded-lg hover:from-green-700 hover:to-emerald-700 transition-all duration-200 shadow-lg"
          >
            🔐 {t('loginHere') || 'Σύνδεση εδώ'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default TrialSignup;
